import { useImpersonation } from '../contexts/ImpersonationContext';
import { useOrganizations } from '../contexts/OrganizationContext';

export interface ActingStoreAdminInput {
  actingOrganizationRole: string | null | undefined;
  actingOrganizationId: string | null | undefined;
  isImpersonating: boolean;
  impersonatedRole: string | null | undefined;
  impersonatedOrgId: string | null | undefined;
}

export interface ActingStoreAdmin {
  /** Owner or admin of the store this session acts for, or a platform admin viewing as that store. */
  canManage: boolean;
  /** The store those rights apply to; the impersonated store wins. */
  organizationId: string | null;
}

/**
 * A platform admin viewing as a store holds no membership there, so
 * `actingOrganization` is null for them; the API treats impersonation as the
 * store's owner on these routes, and so must the UI (MOT-459).
 */
export function resolveActingStoreAdmin(input: ActingStoreAdminInput): ActingStoreAdmin {
  const impersonatingStore = input.isImpersonating && input.impersonatedRole === 'store' && !!input.impersonatedOrgId;
  if (impersonatingStore) return { canManage: true, organizationId: input.impersonatedOrgId ?? null };
  const role = input.actingOrganizationRole;
  return {
    canManage: role === 'owner' || role === 'admin',
    organizationId: input.actingOrganizationId ?? null,
  };
}

export function useActingStoreAdmin(): ActingStoreAdmin {
  const { actingOrganization, actingOrganizationId } = useOrganizations();
  const { isImpersonating, impersonatedRole, impersonatedOrg } = useImpersonation();
  return resolveActingStoreAdmin({
    actingOrganizationRole: actingOrganization?.role,
    actingOrganizationId,
    isImpersonating,
    impersonatedRole,
    impersonatedOrgId: impersonatedOrg?.id,
  });
}
